import { Input } from '@/components/ui/input';

import { VariableAutocomplete } from '../VariableAutocomplete';

import type { CreatePaymentNodeData } from '@repo/shared-types';

interface CreatePaymentConfigProps {
  data: CreatePaymentNodeData;
  onChange: (data: CreatePaymentNodeData) => void;
}

export const CreatePaymentConfig = ({ data, onChange }: CreatePaymentConfigProps) => {
  const update = (fields: Partial<CreatePaymentNodeData>) => {
    onChange({ ...data, ...fields });
  };

  const outputKey = data.outputKey || 'payment';

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-label">
          Label
        </label>
        <Input
          id="cp-label"
          value={data.label || ''}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Create Payment"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-call-id">
          Call ID <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          id="cp-call-id"
          value={data.callId || ''}
          onChange={(v) => update({ callId: v || undefined })}
          placeholder="{{webhook.body.callId}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Links the payment to a call so it appears on the call detail page. Leave blank for
          call-triggered workflows.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-call-sid">
          Call SID <span className="text-[10px] font-normal">(fallback)</span>
        </label>
        <VariableAutocomplete
          id="cp-call-sid"
          value={data.callSid || ''}
          onChange={(v) => update({ callSid: v || undefined })}
          placeholder="{{webhook.body.callSid}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Fallback when Call ID doesn&apos;t match (e.g. WebRTC sessions).
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-vehicle-name">
          Vehicle Name
        </label>
        <VariableAutocomplete
          id="cp-vehicle-name"
          value={data.vehicleName || ''}
          onChange={(v) => update({ vehicleName: v })}
          placeholder="{{call.lead_data.selected_vehicle.name}}"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-registration">
          Registration
        </label>
        <VariableAutocomplete
          id="cp-registration"
          value={data.vehicleRegistration || ''}
          onChange={(v) => update({ vehicleRegistration: v })}
          placeholder="{{call.lead_data.selected_vehicle.registration}}"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-vehicle-id">
          Vehicle ID
        </label>
        <VariableAutocomplete
          id="cp-vehicle-id"
          value={data.vehicleId || ''}
          onChange={(v) => update({ vehicleId: v })}
          placeholder="{{stock.stock_listing_id}}"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-customer-name">
          Customer Name <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          id="cp-customer-name"
          value={data.customerName || ''}
          onChange={(v) => update({ customerName: v || undefined })}
          placeholder="{{call.lead_data.customer_name}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Sent to NatWest as the billing name, so the payment is attributable to a person in their
          back office instead of showing only an order reference. Also stored on the payment record.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-customer-email">
          Customer Email <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          id="cp-customer-email"
          value={data.customerEmail || ''}
          onChange={(v) => update({ customerEmail: v || undefined })}
          placeholder="{{contact.email}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Sent as the billing contact email. Along with the name it feeds the 3D Secure check, which
          usually means fewer challenges for the customer.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-contact-id">
          Contact ID <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          id="cp-contact-id"
          value={data.contactId || ''}
          onChange={(v) => update({ contactId: v || undefined })}
          placeholder="{{contact.id}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          The contact the link belongs to. Leave blank to use the run&apos;s contact.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-config-id">
          Configuration ID <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          id="cp-config-id"
          value={data.configurationId || ''}
          onChange={(v) => update({ configurationId: v || undefined })}
          placeholder="{{config.id}}"
        />
      </div>

      <div>
        <label
          className="mb-1 block text-xs font-medium text-gray-400"
          htmlFor="cp-external-lead-id"
        >
          External Lead ID <span className="text-[10px] font-normal">(optional)</span>
        </label>
        <VariableAutocomplete
          id="cp-external-lead-id"
          value={data.externalLeadId || ''}
          onChange={(v) => update({ externalLeadId: v || undefined })}
          placeholder="{{dealerkit.id}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          The lead&apos;s id in the source system, e.g. DealerKit.
        </p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-source">
          Source
        </label>
        <select
          id="cp-source"
          className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          value={data.source ?? ''}
          onChange={(e) =>
            update({
              source: (e.target.value || undefined) as CreatePaymentNodeData['source'],
            })
          }
        >
          <option value="">Default (workflow)</option>
          <option value="workflow">Workflow</option>
          <option value="call">Call</option>
          <option value="email_campaign">Email campaign</option>
          <option value="whatsapp_campaign">WhatsApp campaign</option>
        </select>
        <p className="mt-1 text-[10px] text-gray-500">Where the payment link was sent from.</p>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-amount">
          Amount
        </label>
        <VariableAutocomplete
          id="cp-amount"
          value={data.amount || ''}
          onChange={(v) => update({ amount: v })}
          placeholder="Leave blank for the default reservation fee"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-phone">
          Phone Number
        </label>
        <VariableAutocomplete
          id="cp-phone"
          value={data.phoneNumber || ''}
          onChange={(v) => update({ phoneNumber: v })}
          placeholder="{{call.phone_number}}"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          Stored on the payment record. This node does not send anything — add a Send SMS node after
          it to deliver the link. Use the <code>raw_url</code> output (not <code>url</code>) in that
          SMS body — Send SMS already wraps links in its own tracking link, so
          <code> url</code> would send a tracking link to a tracking link.
        </p>
      </div>

      <div className="flex items-start gap-2">
        <input
          id="cp-mark-as-sent"
          type="checkbox"
          className="mt-0.5"
          checked={data.markAsSent ?? false}
          onChange={(e) => update({ markAsSent: e.target.checked })}
        />
        <div>
          <label className="text-xs font-medium text-gray-400" htmlFor="cp-mark-as-sent">
            Mark as sent
          </label>
          <p className="text-[10px] text-gray-500">
            Tick when this workflow goes on to deliver the link. Otherwise the payment stays at
            CREATED, which is excluded from payment reporting until it moves to SENT, OPENED, or
            PAID.
          </p>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor="cp-output-key">
          Output Key
        </label>
        <Input
          id="cp-output-key"
          value={data.outputKey || 'payment'}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="payment"
        />
        <p className="mt-1 text-[10px] text-gray-500">
          {`Available downstream as {{${outputKey}.url}}, {{${outputKey}.raw_url}}, {{${outputKey}.amount}}, {{${outputKey}.id}}. `}
          {`Use `}
          <code>{`{{${outputKey}.raw_url}}`}</code>
          {` when sending via a Send SMS node so it creates its own tracking link; `}
          <code>{`{{${outputKey}.url}}`}</code>
          {` is already a tracking link and is best used elsewhere (e.g. email, a webhook payload).`}
        </p>
      </div>
    </div>
  );
};
